import {Environment} from '@coolcolduk/enum';
import {mapStringToEnumValue, throwIfUndefined} from '@coolcolduk/util';
import {StudyStatus} from '@cp/model';
import {AutoAwesome, CheckCircle, Flag} from '@mui/icons-material';
import {ReactElement} from 'react';
import {QuestionStatus, SeedQuestionStatus, StandardSubject, SubjectLevel} from '@cp/model';

const isDev = import.meta.env.VITE_ENVIRONMENT === Environment.DEV;
const isStage = import.meta.env.VITE_ENVIRONMENT === Environment.STAGE;
const isProd = import.meta.env.VITE_ENVIRONMENT === Environment.PROD;

const difficulty = [
  {label: 'Any Difficulty', color: '#1B263B', value: 0},
  {label: 'Very Easy', color: '#90BE6D', value: 1},
  {label: 'Easy', color: '#7AAD5A', value: 2},
  {label: 'Medium', color: '#5A8A3F', value: 3},
  {label: 'Hard', color: '#FF6F59', value: 4},
  {label: 'Very Hard', color: '#E63946', value: 5},
];

/** index of any difficulty in the difficulty array */
const difficultyAnyIndex = 0;

const questionStatus: Record<QuestionStatus, {label: string; color: string}> = {
  [QuestionStatus.GENERATED]: {label: 'Generated', color: '#90BE6D'},
  [QuestionStatus.CHECKED]: {label: 'Checked', color: '#7AAD5A'},
  [QuestionStatus.FLAGGED]: {label: 'Flagged', color: '#FF6F59'},
  [QuestionStatus.REVIEWED]: {label: 'Reviewed', color: '#1B263B'},
  [QuestionStatus.VALIDATED]: {label: 'Validated', color: '#FFB347'},
  [QuestionStatus.OUT_OF_SCOPE]: {label: 'Out of Scope', color: '#E63946'},
  [QuestionStatus.ERROR]: {label: 'Error', color: '#E63946'},
};

const question = {
  status: questionStatus,
  order: [
    QuestionStatus.GENERATED,
    QuestionStatus.CHECKED,
    QuestionStatus.VALIDATED,
    QuestionStatus.REVIEWED,
    QuestionStatus.FLAGGED,
    QuestionStatus.OUT_OF_SCOPE,
    QuestionStatus.ERROR,
  ],
  workflow: [QuestionStatus.GENERATED, QuestionStatus.CHECKED, QuestionStatus.VALIDATED, QuestionStatus.REVIEWED],
  error: [QuestionStatus.FLAGGED, QuestionStatus.ERROR, QuestionStatus.OUT_OF_SCOPE],
  statusNoteLength: 50,
  unknownStatus: {label: 'Unknown', color: '#CED4DA'},
};

const seedQuestionStatus: Record<SeedQuestionStatus, {label: string; color: string}> = {
  [SeedQuestionStatus.GENERATED]: {label: 'Generated', color: '#90BE6D'},
  [SeedQuestionStatus.FLAGGED]: {label: 'Flagged', color: '#FF6F59'},
  [SeedQuestionStatus.ACTIVE]: {label: 'Active', color: '#1B263B'},
};

const seedQuestion = {
  status: seedQuestionStatus,
  unknownStatus: {label: 'Unknown', color: '#CED4DA'},
};

const studyStatus: Record<StudyStatus, {label: string; color: string; icon: ReactElement}> = {
  [StudyStatus.GENERATED]: {label: 'Generated', color: '#90BE6D', icon: <AutoAwesome />},
  [StudyStatus.REVIEWED]: {label: 'Reviewed', color: '#1B263B', icon: <CheckCircle />},
  [StudyStatus.FLAGGED]: {label: 'Flagged', color: '#FF6F59', icon: <Flag />},
};

const study = {
  separator: '## ',
  status: studyStatus,
  unknownStatus: {label: 'Unknown', color: '#CED4DA', icon: (<AutoAwesome />) as ReactElement},
  screenNavigation: {md: 2, lg: 3},
};

const general = {
  reloadPageTimeoutMs: isDev ? 1000 : 10000,
  swipe: {
    minDistance: 50,
  },
};

const practice = {
  defaultQuestions: 5,
};

/** subject colors and icons for UI display */
const subjects: Record<StandardSubject, {label: string; bg: string; color: string; border: string; icon: string}> = {
  MATHEMATICS: {label: 'Maths', bg: '#E3F2FD', color: '#1976D2', border: '#1976D2', icon: '🔢'},
  SCIENCE: {label: 'Science', bg: '#E8F5E9', color: '#388E3C', border: '#388E3C', icon: '🔬'},
  PHYSICS: {label: 'Physics', bg: '#F3E5F5', color: '#7B1FA2', border: '#7B1FA2', icon: '⚛️'},
  CHEMISTRY: {label: 'Chemistry', bg: '#E0F2F1', color: '#00796B', border: '#00796B', icon: '🧪'},
  BIOLOGY: {label: 'Biology', bg: '#E8F5E9', color: '#388E3C', border: '#388E3C', icon: '🌱'},
  HISTORY: {label: 'History', bg: '#FFF3E0', color: '#F57C00', border: '#F57C00', icon: '📜'},
};

const levels: Record<SubjectLevel, {label: string}> = {
  YEAR_7: {label: 'Year 7'},
  YEAR_8: {label: 'Year 8'},
  YEAR_9: {label: 'Year 9'},
  YEAR_10: {label: 'Year 10'},
  YEAR_11: {label: 'Year 11'},
  GCSE: {label: 'GCSE'},
  A_LEVEL: {label: 'A-Level'},
};

const sizeLimit = {
  avatar: 1 * 1024 * 1024,
  background: 2 * 1024 * 1024,
  sound: 20 * 1024,
  questionImage: 1 * 1024 * 1024,
};

export const env = {
  general,
  sizeLimit,
  mode: mapStringToEnumValue(Environment, import.meta.env.VITE_ENVIRONMENT),
  webUrl: throwIfUndefined(import.meta.env.VITE_WEB_URL),
  appUrl: throwIfUndefined(import.meta.env.VITE_APP_URL),
  appName: 'CoachPebble',
  // Firebase configuration
  firebase: {
    apiKey: throwIfUndefined(import.meta.env.VITE_FIREBASE_API_KEY),
    authDomain: throwIfUndefined(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
    projectId: throwIfUndefined(import.meta.env.VITE_FIREBASE_PROJECT_ID),
    storageBucket: throwIfUndefined(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET),
    messagingSenderId: throwIfUndefined(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID),
    appId: throwIfUndefined(import.meta.env.VITE_FIREBASE_APP_ID),
    measurementId: throwIfUndefined(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID),
  },
  firebaseEmulator: {
    auth: import.meta.env.VITE_FIREBASE_AUTH_EMULATOR === 'true',
    firestore: import.meta.env.VITE_FIREBASE_FS_EMULATOR === 'true',
    functions: import.meta.env.VITE_FIREBASE_FN_EMULATOR === 'true',
  },
  resultMotionDurationMs: 4000,
  // Build information
  info: {
    buildTimestamp: import.meta.env.VITE_BUILD_TIMESTAMP || 'Not available',
    commitHash: import.meta.env.VITE_COMMIT_HASH || 'Not available',
    branch: import.meta.env.VITE_BRANCH || 'Not available',
    version: import.meta.env.VITE_VERSION || '1.0.0',
    environment: import.meta.env.MODE || 'development',
    nodeVersion: import.meta.env.VITE_NODE_VERSION || 'Not available',
    buildNumber: import.meta.env.VITE_BUILD_NUMBER,
    deployUrl: import.meta.env.VITE_DEPLOY_URL,
    lastDeploy: import.meta.env.VITE_LAST_DEPLOY,
  },
  label: {
    levelAuto: 'Auto-adjusted',
  },
  isDev,
  isStage,
  isProd,
  trigger: {
    jobProcessor: import.meta.env.VITE_TRIGGER_JOB_PROCESSOR,
  },
  allowUsernamePassword: import.meta.env.VITE_ALLOW_USERNAME_PASSWORD === 'true',
  topic: {
    spacer: '|',
    other: 'Other',
  },
  study,
  score: [
    {color: '#F44336'}, // Red for 0-1
    {color: '#FF9800'}, // Orange for 1-2
    {color: '#FFC107'}, // Amber for 2-3
    {color: '#4CAF50'}, // Green for 3-4
    {color: '#2196F3'}, // Blue for 4-5
  ],
  subjects,
  levels,
  practice,
  difficulty,
  difficultyAnyIndex,
  question,
  questionReview: {
    /** use this question id when no question id is provided in the url */
    noQuestionIs: 'question',
  },
  seedQuestion,
};
